import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { MoreHorizontal, ExternalLink } from "lucide-react";

import { RfqAttachmentsPanel } from "@/components/mrp/RfqAttachmentsPanel";
import { RfqLineDocumentAccessRow } from "@/components/mrp/RfqAttachDocumentModal";
import { RfqEmailModal } from "@/components/mrp/RfqEmailModal";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { DeferredCommitStatusGlyph } from "@/components/mrp/DeferredCommitStatusGlyph";
import { HubPageTitle } from "@/components/HubPageTitle";
import { PurchaseOrderHubLinkWithMegaventory } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SupplierSearchableSelect } from "@/components/mrp/SupplierSearchableSelect";
import { PageTitleWithHelp, HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";
import { prefillLineDescriptionFromSkuCatalog } from "@/lib/purchaseOrderDraftForm";
import {
  formatNumber,
  formatPoMoney,
  formatPoMoneyWithSymbol,
  poCurrencySymbol,
} from "@/lib/mrp";
import { apiFetch } from "@/lib/api";
import { freshdeskTicketUrl } from "@/lib/freshdeskAgentUrl";
import {
  MRP_RFQ_STATUS_HOVER_HINT,
  MRP_RFQ_STATUS_LABEL,
  MRP_RFQ_ASSIGNEES_QUERY_KEY,
  addMrpRfqFreshdeskTicket,
  cancelMrpRfq,
  createMrpRfq,
  createPurchaseOrderComposeFromMrpRfq,
  attachMrpRfqDocumentsFromLibrary,
  deleteMrpRfqAttachment,
  downloadMrpRfqAttachment,
  emptyRfqLineInput,
  emptyRfqTargetInput,
  fetchMrpRfqAssignees,
  fetchMrpRfq,
  mergeMrpRfqAssigneePatch,
  parseRfqTargetQuantityInput,
  patchMrpRfqHeader,
  removeMrpRfqFreshdeskTicket,
  resolveMrpRfqAssigneeDisplayName,
  rfqTargetQuantityAsNumber,
  syncMrpRfqAssigneeListCaches,
  saveMrpRfqQuote,
  saveMrpRfqQuoteSelections,
  sendMrpRfq,
  takeOverMrpRfq,
  uploadMrpRfqAttachment,
  mrpRfqStatusBadgeTone,
  type MrpRfq,
  type MrpRfqAttachment,
  type MrpRfqAttachmentAudience,
  type MrpRfqLine,
  type MrpRfqLineInput,
  type MrpRfqQuoteLineInput,
  type MrpRfqTargetInput,
} from "@/lib/rfqApi";
import { useIntegratorMe } from "@/lib/me-query";
import type { RfqAttachmentUploadProgress, RfqAttachmentUploadTarget } from "@/lib/rfqAttachmentUpload";
import {
  formatRfqAttachmentUploadError,
  rfqAttachmentUploadTargetKey,
} from "@/lib/rfqAttachmentUpload";
import { useHubModuleHasAnyAccess, useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { mrpRfqUserIdsMatch } from "./rfqAssigneePatch";
import {
  clearRfqComposeSessionDraft,
  loadRfqComposeSessionBootstrap,
  normalizeRfqComposeLines,
  persistRfqComposeSessionDraft,
  rfqDraftDirtyFieldLabels,
  rfqDraftSnapshotFromRfq,
  rfqLineInputsFromRfq,
} from "./rfqComposeSessionCache";
import { useRfqDraftAutosave } from "./useRfqDraftAutosave";
import { QualityFreshdeskTicketPicker } from "@/pages/quality/QualityFreshdeskTicketPicker";

function rfqAttachmentUploadStatus(progress: RfqAttachmentUploadProgress): string {
  if (progress.phase === "queued") {
    return progress.position == null
      ? "Waiting for upload capacity"
      : `Waiting for upload capacity, position ${progress.position}`;
  }
  if (progress.phase === "finalizing") {
    return "Finalizing attachment";
  }
  return `Uploading attachment, ${progress.percent ?? 0}%`;
}

const RFQ_HEADER_SUPPLIER_DOCS_HOVER =
  "Attached to the outbound RFQ email (drawings, specs, and similar).";
const RFQ_LINE_SUPPLIER_DOCS_HOVER = "Included when you email this RFQ.";
const RFQ_HEADER_INTERNAL_DOCS_HOVER =
  "Hub-only files such as the supplier quote PDF after you receive it.";
const RFQ_LINE_INTERNAL_DOCS_HOVER = "Hub-only files for this line.";

function composeStagedDocumentsEmptyMessage(stagedCount: number): string | null {
  if (stagedCount <= 0) {
    return null;
  }
  return `${stagedCount} document${stagedCount === 1 ? "" : "s"} staged (copied onto the RFQ when you create it).`;
}

function mergeComposeStagedDocumentIds(
  current: Record<number, string[]>,
  lineIndex: number,
  documentRecordIds: string[],
): Record<number, string[]> {
  const merged = [...new Set([...(current[lineIndex] ?? []), ...documentRecordIds])];
  return { ...current, [lineIndex]: merged };
}

type QuoteRowForm = {
  clientKey: string;
  quantity: string;
  quoted_unit_price: string;
  quoted_lead_time_weeks: string;
};

function isComposeRoute(id: string | undefined): boolean {
  return id === "compose";
}

type RfqTargetField = "quantity" | "price";

function rfqTargetInputId(lineIndex: number, targetIndex: number, field: RfqTargetField): string {
  return `rfq-target-${lineIndex}-${targetIndex}-${field}`;
}

function focusRfqTargetInput(lineIndex: number, targetIndex: number, field: RfqTargetField): void {
  requestAnimationFrame(() => {
    const element = document.getElementById(rfqTargetInputId(lineIndex, targetIndex, field));
    if (element instanceof HTMLInputElement) {
      element.focus();
      element.select();
    }
  });
}

function composeBootstrapForMount(id: string | undefined) {
  return isComposeRoute(id) ? loadRfqComposeSessionBootstrap() : null;
}

type RfqQuoteField = "quantity" | "quoted_unit_price" | "quoted_lead_time_weeks";

function selectInputContents(event: FocusEvent<HTMLInputElement>): void {
  event.currentTarget.select();
}

function rfqQuoteQuantityFocusKey(lineNumber: number, quoteIndex: number): string {
  return `${lineNumber}-${quoteIndex}`;
}

function parseQuoteQuantityInput(raw: string): string {
  return raw.replace(/,/g, "").replace(/[^\d]/g, "");
}

function formatQuoteQuantityInput(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") return "";
  const numeric = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(numeric) || numeric <= 0) return trimmed;
  return formatNumber(numeric);
}

function formatTargetQuantityInput(raw: number | string): string {
  return formatQuoteQuantityInput(String(raw ?? ""));
}

function rfqTargetQuantityFocusKey(lineIndex: number, targetIndex: number): string {
  return `target-${lineIndex}-${targetIndex}`;
}

function parseQuotePriceInput(raw: string): string {
  const stripped = raw.replace(/,/g, "").replace(/[^\d.]/g, "");
  const parts = stripped.split(".");
  if (parts.length <= 1) return stripped;
  return `${parts[0]}.${parts.slice(1).join("")}`;
}

function formatQuoteLeadTimeWeeks(value: number | string | null | undefined): string {
  if (value == null || value === "") return "—";
  const numeric = typeof value === "number" ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(numeric)) return "—";
  return `${numeric} wks`;
}

function rfqQuoteInputId(lineNumber: number, quoteIndex: number, field: RfqQuoteField): string {
  return `rfq-quote-${lineNumber}-${quoteIndex}-${field}`;
}

function focusRfqQuoteInput(lineNumber: number, quoteIndex: number, field: RfqQuoteField): void {
  requestAnimationFrame(() => {
    const element = document.getElementById(rfqQuoteInputId(lineNumber, quoteIndex, field));
    if (element instanceof HTMLInputElement) {
      element.focus();
      element.select();
    }
  });
}

function emptyQuoteRowForm(clientKey: string, leadTimeWeeks = ""): QuoteRowForm {
  return {
    clientKey,
    quantity: "",
    quoted_unit_price: "",
    quoted_lead_time_weeks: leadTimeWeeks,
  };
}

function nextClientKey(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function RfqQuoteMatchBadge({ matchesTarget }: { matchesTarget: boolean }): JSX.Element {
  return (
    <span className="inline-grid [grid-template-areas:'stack']">
      <Badge tone="success" className="invisible [grid-area:stack]" aria-hidden>
        Matches target
      </Badge>
      <span className="[grid-area:stack]">
        {matchesTarget ? (
          <Badge tone="success">Matches target</Badge>
        ) : (
          <Badge tone="muted">Other qty</Badge>
        )}
      </span>
    </span>
  );
}

export function RfqDetail() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const documentsHubAccess = useHubModuleHasAnyAccess("documents");
  const meQuery = useIntegratorMe();
  const currentUserId = meQuery.data?.user.id?.trim() ?? "";
  const composeMode = isComposeRoute(idParam);
  const rfqId = composeMode || idParam == null ? NaN : Number.parseInt(idParam, 10);
  const composeBootstrap = composeBootstrapForMount(idParam);
  const composeSessionHydratedRef = useRef(composeBootstrap != null);
  const pendingTargetFocusRef = useRef<{
    lineIndex: number;
    targetIndex: number;
    field: RfqTargetField;
  } | null>(null);

  const [supplierId, setSupplierId] = useState(() => composeBootstrap?.supplierId ?? "");
  const [supplierLabel, setSupplierLabel] = useState<string | null>(
    () => composeBootstrap?.supplierLabel ?? null,
  );
  const [supplierSearch, setSupplierSearch] = useState(() => composeBootstrap?.supplierSearch ?? "");
  const pendingQuoteFocusRef = useRef<{
    lineNumber: number;
    quoteIndex: number;
    field: RfqQuoteField;
  } | null>(null);

  const [internalNotes, setInternalNotes] = useState(() => composeBootstrap?.internalNotes ?? "");
  const [externalNotes, setExternalNotes] = useState(() => composeBootstrap?.externalNotes ?? "");
  const [neededByDate, setNeededByDate] = useState(() => composeBootstrap?.neededByDate ?? "");
  const [currency, setCurrency] = useState(() => composeBootstrap?.currency ?? "USD");
  const [lines, setLines] = useState<MrpRfqLineInput[]>(
    () => normalizeRfqComposeLines(composeBootstrap?.lines ?? [emptyRfqLineInput()]),
  );
  const [composeStagedExternalDocumentsByLineIndex, setComposeStagedExternalDocumentsByLineIndex] =
    useState<Record<number, string[]>>(
      () => composeBootstrap?.stagedExternalDocumentsByLineIndex ?? {},
    );
  const [composeStagedInternalDocumentsByLineIndex, setComposeStagedInternalDocumentsByLineIndex] =
    useState<Record<number, string[]>>(
      () => composeBootstrap?.stagedInternalDocumentsByLineIndex ?? {},
    );
  const [supplierQuoteReference, setSupplierQuoteReference] = useState("");
  const [quoteValidUntil, setQuoteValidUntil] = useState("");
  const [quoteRowsByLine, setQuoteRowsByLine] = useState<Record<number, QuoteRowForm[]>>({});
  const [focusedQuoteQuantityKey, setFocusedQuoteQuantityKey] = useState<string | null>(null);
  const [focusedTargetQuantityKey, setFocusedTargetQuantityKey] = useState<string | null>(null);
  const [selectedQuoteIds, setSelectedQuoteIds] = useState<Record<number, number | null>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [attachmentUploadStatus, setAttachmentUploadStatus] = useState<string | null>(null);
  const [attachmentUploadTarget, setAttachmentUploadTarget] = useState<RfqAttachmentUploadTarget | null>(
    null,
  );
  const [attachmentUploadError, setAttachmentUploadError] = useState<{
    key: string;
    message: string;
  } | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [overflowMenuPos, setOverflowMenuPos] = useState<AnchoredMenuPanelBox | null>(null);
  const [savedDraftSnapshot, setSavedDraftSnapshot] = useState<string | null>(null);
  const [rfqFormHydrated, setRfqFormHydrated] = useState(false);
  const overflowMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  const rfqQuery = useQuery({
    queryKey: ["mrp", "rfqs", rfqId],
    queryFn: () => fetchMrpRfq(rfqId),
    enabled: Number.isFinite(rfqId) && rfqId > 0,
  });

  const assigneeOptionsQuery = useQuery({
    queryKey: MRP_RFQ_ASSIGNEES_QUERY_KEY,
    queryFn: fetchMrpRfqAssignees,
    enabled: !composeMode && hubWritesAllowed,
  });

  const supplierCurrencyQuery = useQuery({
    queryKey: ["mrp", "supplier-currency", supplierId],
    enabled: composeMode && supplierId.trim() !== "",
    queryFn: async () => {
      const response = await apiFetch<{ rows: Array<{ id: number; currency?: string | null }> }>(
        "/api/mrp/suppliers-for-purchase-order",
        { query: { q: supplierId } },
      );
      return response.rows.find((row) => String(row.id) === supplierId)?.currency ?? null;
    },
  });

  useEffect(() => {
    if (!composeMode) return;
    const supplierCurrency = supplierCurrencyQuery.data?.trim();
    if (supplierCurrency != null && supplierCurrency !== "") {
      setCurrency(supplierCurrency.toUpperCase());
    }
  }, [composeMode, supplierCurrencyQuery.data]);

  const rfq = rfqQuery.data;
  const isDraft = composeMode || rfq?.status === "draft";

  useEffect(() => {
    if (!composeMode) {
      composeSessionHydratedRef.current = false;
      return;
    }
    if (composeSessionHydratedRef.current) return;
    composeSessionHydratedRef.current = true;
    const boot = loadRfqComposeSessionBootstrap();
    setSupplierId(boot.supplierId);
    setSupplierLabel(boot.supplierLabel);
    setSupplierSearch(boot.supplierSearch);
    setInternalNotes(boot.internalNotes);
    setExternalNotes(boot.externalNotes);
    setNeededByDate(boot.neededByDate);
    setCurrency(boot.currency);
    setLines(
      normalizeRfqComposeLines(boot.lines).map((line) => ({
        ...line,
        targets: line.targets.map((target) => ({ ...target })),
      })),
    );
    setComposeStagedExternalDocumentsByLineIndex(boot.stagedExternalDocumentsByLineIndex);
    setComposeStagedInternalDocumentsByLineIndex(boot.stagedInternalDocumentsByLineIndex);
  }, [composeMode]);

  useEffect(() => {
    if (!composeMode) return;
    const timeoutId = window.setTimeout(() => {
      persistRfqComposeSessionDraft({
        v: 1,
        savedAtIso: new Date().toISOString(),
        supplierId,
        supplierLabel,
        supplierSearch,
        internalNotes,
        externalNotes,
        neededByDate,
        currency,
        lines,
        stagedExternalDocumentsByLineIndex: composeStagedExternalDocumentsByLineIndex,
        stagedInternalDocumentsByLineIndex: composeStagedInternalDocumentsByLineIndex,
      });
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [
    composeMode,
    supplierId,
    supplierLabel,
    supplierSearch,
    internalNotes,
    externalNotes,
    neededByDate,
    currency,
    lines,
    composeStagedExternalDocumentsByLineIndex,
    composeStagedInternalDocumentsByLineIndex,
  ]);
  const canEditAttachments = rfq != null && rfq.status !== "cancelled";
  const canEnterQuote = rfq?.status === "sent" || rfq?.status === "quoted";
  const canPickQuoteForPo = rfq?.status === "quoted";
  const readOnly = rfq != null && (rfq.status === "converted" || rfq.status === "cancelled");

  const headerExternalAttachments = useMemo(
    () =>
      (rfq?.attachments ?? []).filter(
        (attachment) =>
          attachment.audience === "external" && attachment.request_for_quote_item_id == null,
      ),
    [rfq?.attachments],
  );
  const headerInternalAttachments = useMemo(
    () =>
      (rfq?.attachments ?? []).filter(
        (attachment) =>
          attachment.audience === "internal" && attachment.request_for_quote_item_id == null,
      ),
    [rfq?.attachments],
  );
  const attachmentsByLineId = useMemo(() => {
    const map = new Map<number, { external: MrpRfqAttachment[]; internal: MrpRfqAttachment[] }>();
    for (const attachment of rfq?.attachments ?? []) {
      if (attachment.request_for_quote_item_id == null) {
        continue;
      }
      const entry = map.get(attachment.request_for_quote_item_id) ?? { external: [], internal: [] };
      if (attachment.audience === "external") {
        entry.external.push(attachment);
      } else {
        entry.internal.push(attachment);
      }
      map.set(attachment.request_for_quote_item_id, entry);
    }
    return map;
  }, [rfq?.attachments]);

  const isAssignedToCurrentUser =
    currentUserId !== ""
    && rfq?.assigned_to_user_id != null
    && mrpRfqUserIdsMatch(rfq.assigned_to_user_id, currentUserId);

  const assigneeOptions = useMemo(() => {
    const users = assigneeOptionsQuery.data?.users ?? [];
    const options = users.map((user) => ({
      userId: user.id,
      label: user.display_name?.trim() ? user.display_name : user.id,
    }));
    const currentAssigneeId = rfq?.assigned_to_user_id?.trim() ?? "";
    if (
      currentAssigneeId !== ""
      && !options.some((option) => option.userId === currentAssigneeId)
      && rfq?.assigned_to_display_name
    ) {
      options.unshift({
        userId: currentAssigneeId,
        label: rfq.assigned_to_display_name,
      });
    }
    return options;
  }, [
    assigneeOptionsQuery.data?.users,
    rfq?.assigned_to_display_name,
    rfq?.assigned_to_user_id,
  ]);

  useEffect(() => {
    if (rfq == null || composeMode) {
      setRfqFormHydrated(false);
      return;
    }
    setInternalNotes(rfq.internal_notes ?? "");
    setExternalNotes(rfq.external_notes ?? "");
    setNeededByDate(rfq.needed_by_date ?? "");
    setCurrency(rfq.currency);
    setSupplierQuoteReference(rfq.supplier_quote_reference ?? "");
    setQuoteValidUntil(rfq.quote_valid_until ?? "");
    setLines(rfqLineInputsFromRfq(rfq));
    setSavedDraftSnapshot(rfqDraftSnapshotFromRfq(rfq));
    setRfqFormHydrated(true);
  }, [composeMode, rfq?.id]);

  useEffect(() => {
    if (rfq == null) return;
    const quoteMap: Record<number, QuoteRowForm[]> = {};
    const selectionMap: Record<number, number | null> = {};
    for (const line of rfq.lines) {
      quoteMap[line.line_number] =
        line.quotes.length > 0
          ? line.quotes.map((quote) => ({
              clientKey: String(quote.id),
              quantity: String(quote.quantity),
              quoted_unit_price: quote.quoted_unit_price == null ? "" : String(quote.quoted_unit_price),
              quoted_lead_time_weeks:
                quote.quoted_lead_time_weeks == null ? "" : String(quote.quoted_lead_time_weeks),
            }))
          : [emptyQuoteRowForm(nextClientKey(`line-${line.line_number}`))];
      if (line.selected_quote_id != null) {
        selectionMap[line.line_number] = line.selected_quote_id;
      }
    }
    setQuoteRowsByLine(quoteMap);
    setSelectedQuoteIds(selectionMap);
  }, [rfq?.id, rfq?.updated_at]);

  const invalidateRfqQueries = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["mrp", "rfqs"] });
    if (Number.isFinite(rfqId) && rfqId > 0) {
      await queryClient.invalidateQueries({ queryKey: ["mrp", "rfqs", rfqId] });
    }
  }, [queryClient, rfqId]);

  const validateSubmitLines = useCallback((submitLines: MrpRfqLineInput[]): void => {
    if (submitLines.length === 0) {
      throw new Error("Add at least one line with a SKU.");
    }
    for (const [index, line] of submitLines.entries()) {
      const targets = line.targets.filter((target) => rfqTargetQuantityAsNumber(target.quantity) > 0);
      if (targets.length === 0) {
        throw new Error(`Line ${index + 1} needs at least one target quantity.`);
      }
    }
  }, []);

  const validateRfqDraftAutosaveLines = useCallback((submitLines: MrpRfqLineInput[]): void => {
    if (submitLines.length === 0) {
      return;
    }
    for (const [index, line] of submitLines.entries()) {
      const targets = line.targets.filter((target) => rfqTargetQuantityAsNumber(target.quantity) > 0);
      if (targets.length === 0) {
        throw new Error(`Line ${index + 1} needs at least one target quantity.`);
      }
    }
  }, []);

  const normalizeSubmitLines = useCallback(
    (raw: MrpRfqLineInput[]) =>
      normalizeRfqComposeLines(raw).filter((line) => line.sku.trim() !== ""),
    [],
  );

  const rfqDraftAutosaveEnabled =
    hubWritesAllowed && !composeMode && rfq != null && rfq.status === "draft";

  const {
    draftAutosaveStatus,
    draftAutosaveError,
    draftDirty,
    draftSavePending,
    draftSaveSucceeded,
    handleRfqDraftFieldBlur,
  } = useRfqDraftAutosave({
    enabled: rfqDraftAutosaveEnabled,
    formReady: rfqFormHydrated,
    rfq,
    rfqId,
    internalNotes,
    externalNotes,
    neededByDate,
    currency,
    lines,
    savedDraftSnapshot,
    setSavedDraftSnapshot,
    validateSubmitLines: validateRfqDraftAutosaveLines,
    normalizeSubmitLines,
    onPersisted: invalidateRfqQueries,
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const supplierNumeric = Number.parseInt(supplierId, 10);
      if (!Number.isFinite(supplierNumeric) || supplierNumeric <= 0) {
        throw new Error("Select a supplier.");
      }
      const submitLines = normalizeRfqComposeLines(lines).filter((line) => line.sku.trim() !== "");
      validateSubmitLines(submitLines);
      return createMrpRfq({
        supplier_id: supplierNumeric,
        internal_notes: internalNotes.trim() || null,
        external_notes: externalNotes.trim() || null,
        needed_by_date: neededByDate.trim() || null,
        currency,
        lines: submitLines,
      });
    },
    onSuccess: async (created) => {
      setActionError(null);
      const submitLines = normalizeRfqComposeLines(lines).filter((line) => line.sku.trim() !== "");
      try {
        for (let lineIndex = 0; lineIndex < submitLines.length; lineIndex++) {
          const createdLine = created.lines.find((line) => line.line_number === lineIndex + 1);
          if (createdLine == null) continue;

          const externalDocumentRecordIds = composeStagedExternalDocumentsByLineIndex[lineIndex];
          if (externalDocumentRecordIds != null && externalDocumentRecordIds.length > 0) {
            await attachMrpRfqDocumentsFromLibrary(created.id, {
              documentRecordIds: externalDocumentRecordIds,
              audience: "external",
              lineId: createdLine.id,
            });
          }

          const internalDocumentRecordIds = composeStagedInternalDocumentsByLineIndex[lineIndex];
          if (internalDocumentRecordIds != null && internalDocumentRecordIds.length > 0) {
            await attachMrpRfqDocumentsFromLibrary(created.id, {
              documentRecordIds: internalDocumentRecordIds,
              audience: "internal",
              lineId: createdLine.id,
            });
          }
        }
        clearRfqComposeSessionDraft();
        setComposeStagedExternalDocumentsByLineIndex({});
        setComposeStagedInternalDocumentsByLineIndex({});
        await invalidateRfqQueries();
        navigate(`/mrp/rfqs/${created.id}`, { replace: true });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Could not attach staged documents.";
        setActionError(
          `RFQ ${created.rfq_number} was created, but staged documents could not be attached: ${message} Open the RFQ to retry from the line attachments.`,
        );
        clearRfqComposeSessionDraft();
        setComposeStagedExternalDocumentsByLineIndex({});
        setComposeStagedInternalDocumentsByLineIndex({});
        await invalidateRfqQueries();
        navigate(`/mrp/rfqs/${created.id}`, { replace: true });
      }
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendMrpRfq(rfqId),
    onSuccess: async () => {
      setActionError(null);
      await invalidateRfqQueries();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const quoteMutation = useMutation({
    mutationFn: () => {
      if (rfq == null) throw new Error("RFQ not loaded.");
      const quoteLines: MrpRfqQuoteLineInput[] = rfq.lines.map((line) => ({
        line_number: line.line_number,
        quotes: (quoteRowsByLine[line.line_number] ?? [])
          .map((row) => ({
            quantity: Number.parseFloat(row.quantity.replace(/,/g, "")) || 0,
            quoted_unit_price:
              row.quoted_unit_price.trim() === "" ? null : Number.parseFloat(row.quoted_unit_price),
            quoted_lead_time_weeks:
              row.quoted_lead_time_weeks.trim() === ""
                ? null
                : Number.parseInt(row.quoted_lead_time_weeks, 10),
          }))
          .filter((quote) => quote.quantity > 0),
      }));
      return saveMrpRfqQuote(rfqId, {
        supplier_quote_reference: supplierQuoteReference.trim() || null,
        quote_valid_until: quoteValidUntil.trim() || null,
        lines: quoteLines,
      });
    },
    onSuccess: async () => {
      setActionError(null);
      await invalidateRfqQueries();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const createPoMutation = useMutation({
    mutationFn: async () => {
      if (rfq == null) throw new Error("RFQ not loaded.");
      const selections = rfq.lines
        .map((line) => {
          const explicitSelection = selectedQuoteIds[line.line_number];
          if (explicitSelection === null) {
            return null;
          }
          const quoteId = explicitSelection ?? line.selected_quote_id;
          if (quoteId == null) {
            return null;
          }
          return { line_number: line.line_number, quote_id: quoteId };
        })
        .filter((selection): selection is { line_number: number; quote_id: number } => selection != null);

      if (selections.length === 0) {
        throw new Error(
          "Select at least one quoted line for the purchase order, or mark lines as skip.",
        );
      }

      if (
        selections.some(
          (selection) =>
            selection.quote_id
            !== rfq.lines.find((line) => line.line_number === selection.line_number)?.selected_quote_id,
        )
      ) {
        await saveMrpRfqQuoteSelections(rfqId, selections);
      }

      return createPurchaseOrderComposeFromMrpRfq(rfqId, selections);
    },
    onSuccess: async (result) => {
      setActionError(null);
      await invalidateRfqQueries();
      navigate(result.compose_href);
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelMrpRfq(rfqId),
    onSuccess: async () => {
      setActionError(null);
      await invalidateRfqQueries();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const uploadMutation = useMutation({
    mutationFn: (input: {
      file: File;
      audience: MrpRfqAttachmentAudience;
      lineId?: number | null;
    }) =>
      uploadMrpRfqAttachment(rfqId, input.file, {
        audience: input.audience,
        lineId: input.lineId,
        onProgress: (progress) => {
          setAttachmentUploadStatus(rfqAttachmentUploadStatus(progress));
        },
      }),
    onMutate: (input) => {
      const target: RfqAttachmentUploadTarget = {
        audience: input.audience,
        lineId: input.lineId ?? null,
      };
      setAttachmentUploadTarget(target);
      setAttachmentUploadError(null);
      setAttachmentUploadStatus("Requesting upload capacity");
    },
    onSuccess: async () => {
      setActionError(null);
      setAttachmentUploadError(null);
      await invalidateRfqQueries();
    },
    onError: async (error: Error, input) => {
      const message = formatRfqAttachmentUploadError(error);
      setAttachmentUploadError({
        key: rfqAttachmentUploadTargetKey({
          audience: input.audience,
          lineId: input.lineId ?? null,
        }),
        message,
      });
      showToast({ tone: "error", message });
      await invalidateRfqQueries();
    },
    onSettled: () => {
      setAttachmentUploadStatus(null);
      setAttachmentUploadTarget(null);
    },
  });

  const attachDocumentMutation = useMutation({
    mutationFn: async (input: {
      documentRecordIds: string[];
      audience: MrpRfqAttachmentAudience;
      lineId?: number | null;
    }) => {
      await attachMrpRfqDocumentsFromLibrary(rfqId, {
        documentRecordIds: input.documentRecordIds,
        audience: input.audience,
        lineId: input.lineId,
      });
    },
    onSuccess: async () => {
      setActionError(null);
      await invalidateRfqQueries();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const attachmentPanelUpload = (target: RfqAttachmentUploadTarget) => {
    const key = rfqAttachmentUploadTargetKey(target);
    const isThisUpload =
      attachmentUploadTarget != null &&
      rfqAttachmentUploadTargetKey(attachmentUploadTarget) === key;
    const attachingVariables = attachDocumentMutation.variables;
    const isThisAttach =
      attachDocumentMutation.isPending &&
      attachingVariables != null &&
      attachingVariables.audience === target.audience &&
      (attachingVariables.lineId ?? null) === target.lineId;
    return {
      isUploading: isThisUpload && uploadMutation.isPending,
      uploadStatus: isThisUpload ? attachmentUploadStatus : null,
      uploadError: attachmentUploadError?.key === key ? attachmentUploadError.message : null,
      isAttachingFromDocument: isThisAttach,
    };
  };

  const handleRfqAttachmentDownload = useCallback(
    async (attachment: MrpRfqAttachment) => {
      if (rfq == null) {
        return;
      }
      const result = await downloadMrpRfqAttachment(rfq.id, attachment.id);
      window.open(result.url, "_blank", "noopener,noreferrer");
    },
    [rfq],
  );

  const handleRfqAttachmentPreview = useCallback(
    (attachment: MrpRfqAttachment) => {
      if (rfq == null) {
        return Promise.reject(new Error("RFQ not loaded."));
      }
      return downloadMrpRfqAttachment(rfq.id, attachment.id);
    },
    [rfq],
  );

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: number) => deleteMrpRfqAttachment(rfqId, attachmentId),
    onSuccess: async () => {
      setActionError(null);
      await invalidateRfqQueries();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const assignMutation = useMutation({
    mutationFn: (assignedToUserId: string | null) =>
      patchMrpRfqHeader(rfqId, { assigned_to_user_id: assignedToUserId }),
    onMutate: async (assignedToUserId) => {
      await queryClient.cancelQueries({ queryKey: ["mrp", "rfqs", rfqId] });
      const previous = queryClient.getQueryData<MrpRfq>(["mrp", "rfqs", rfqId]);
      if (previous == null) {
        return { previous: undefined as MrpRfq | undefined };
      }
      const displayName = resolveMrpRfqAssigneeDisplayName(
        assignedToUserId,
        assigneeOptionsQuery.data?.users ?? [],
      );
      queryClient.setQueryData<MrpRfq>(["mrp", "rfqs", rfqId], {
        ...previous,
        assigned_to_user_id: assignedToUserId,
        assigned_to_display_name: displayName,
      });
      return { previous };
    },
    onSuccess: (updatedRfq) => {
      setActionError(null);
      queryClient.setQueryData<MrpRfq>(["mrp", "rfqs", rfqId], (current) =>
        current == null ? updatedRfq : mergeMrpRfqAssigneePatch(current, updatedRfq),
      );
      syncMrpRfqAssigneeListCaches(queryClient, rfqId, {
        assigned_to_user_id: updatedRfq.assigned_to_user_id,
        assigned_to_display_name: updatedRfq.assigned_to_display_name,
      });
    },
    onError: (error: Error, _assignedToUserId, context) => {
      setActionError(error.message);
      if (context?.previous != null) {
        queryClient.setQueryData(["mrp", "rfqs", rfqId], context.previous);
      }
    },
  });

  const takeOverMutation = useMutation({
    mutationFn: () => takeOverMrpRfq(rfqId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["mrp", "rfqs", rfqId] });
      const previous = queryClient.getQueryData<MrpRfq>(["mrp", "rfqs", rfqId]);
      if (previous == null || currentUserId === "") {
        return { previous: undefined as MrpRfq | undefined };
      }
      const displayName = resolveMrpRfqAssigneeDisplayName(
        currentUserId,
        assigneeOptionsQuery.data?.users ?? [],
      );
      queryClient.setQueryData<MrpRfq>(["mrp", "rfqs", rfqId], {
        ...previous,
        assigned_to_user_id: currentUserId,
        assigned_to_display_name: displayName ?? previous.assigned_to_display_name,
      });
      return { previous };
    },
    onSuccess: (updatedRfq) => {
      setActionError(null);
      queryClient.setQueryData<MrpRfq>(["mrp", "rfqs", rfqId], (current) =>
        current == null ? updatedRfq : mergeMrpRfqAssigneePatch(current, updatedRfq),
      );
      syncMrpRfqAssigneeListCaches(queryClient, rfqId, {
        assigned_to_user_id: updatedRfq.assigned_to_user_id,
        assigned_to_display_name: updatedRfq.assigned_to_display_name,
      });
    },
    onError: (error: Error, _vars, context) => {
      setActionError(error.message);
      if (context?.previous != null) {
        queryClient.setQueryData(["mrp", "rfqs", rfqId], context.previous);
      }
    },
  });

  const updateLine = (lineIndex: number, patch: Partial<MrpRfqLineInput>): void => {
    setLines((current) =>
      current.map((entry, entryIndex) => (entryIndex === lineIndex ? { ...entry, ...patch } : entry)),
    );
  };

  const updateLineTarget = (
    lineIndex: number,
    targetIndex: number,
    patch: Partial<MrpRfqTargetInput>,
  ): void => {
    setLines((current) =>
      current.map((entry, entryIndex) => {
        if (entryIndex !== lineIndex) return entry;
        return {
          ...entry,
          targets: entry.targets.map((target, index) =>
            index === targetIndex ? { ...target, ...patch } : target,
          ),
        };
      }),
    );
  };

  const addLineTarget = (lineIndex: number): void => {
    setLines((current) =>
      current.map((entry, entryIndex) =>
        entryIndex === lineIndex
          ? { ...entry, targets: [...entry.targets, emptyRfqTargetInput()] }
          : entry,
      ),
    );
  };

  useEffect(() => {
    const pending = pendingTargetFocusRef.current;
    if (pending == null) {
      return;
    }
    pendingTargetFocusRef.current = null;
    focusRfqTargetInput(pending.lineIndex, pending.targetIndex, pending.field);
  }, [lines]);

  const handleTargetInputKeyDown = (
    event: KeyboardEvent,
    lineIndex: number,
    targetIndex: number,
    field: RfqTargetField,
  ): void => {
    const line = lines[lineIndex];
    if (line == null) {
      return;
    }
    const targetCount = line.targets.length;
    const isLastTarget = targetIndex === targetCount - 1;

    if (event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      if (field === "quantity") {
        focusRfqTargetInput(lineIndex, targetIndex, "price");
        return;
      }
      if (!isLastTarget) {
        focusRfqTargetInput(lineIndex, targetIndex + 1, "quantity");
        return;
      }
      pendingTargetFocusRef.current = {
        lineIndex,
        targetIndex: targetCount,
        field: "quantity",
      };
      addLineTarget(lineIndex);
      return;
    }

    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();

    if (!isLastTarget) {
      focusRfqTargetInput(lineIndex, targetIndex + 1, "quantity");
      return;
    }

    pendingTargetFocusRef.current = {
      lineIndex,
      targetIndex: targetCount,
      field: "quantity",
    };
    addLineTarget(lineIndex);
  };

  const removeLineTarget = (lineIndex: number, targetIndex: number): void => {
    setLines((current) =>
      current.map((entry, entryIndex) => {
        if (entryIndex !== lineIndex) return entry;
        if (entry.targets.length <= 1) return entry;
        return {
          ...entry,
          targets: entry.targets.filter((_, index) => index !== targetIndex),
        };
      }),
    );
  };

  const updateQuoteRow = (
    lineNumber: number,
    rowIndex: number,
    patch: Partial<QuoteRowForm>,
  ): void => {
    setQuoteRowsByLine((current) => ({
      ...current,
      [lineNumber]: (current[lineNumber] ?? []).map((row, index) =>
        index === rowIndex ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addQuoteRow = (lineNumber: number): void => {
    setQuoteRowsByLine((current) => {
      const rows = current[lineNumber] ?? [];
      const lastLeadTimeWeeks = rows[rows.length - 1]?.quoted_lead_time_weeks.trim() ?? "";
      return {
        ...current,
        [lineNumber]: [
          ...rows,
          emptyQuoteRowForm(nextClientKey(`quote-${lineNumber}`), lastLeadTimeWeeks),
        ],
      };
    });
  };

  useEffect(() => {
    const pending = pendingQuoteFocusRef.current;
    if (pending == null) {
      return;
    }
    pendingQuoteFocusRef.current = null;
    focusRfqQuoteInput(pending.lineNumber, pending.quoteIndex, pending.field);
  }, [quoteRowsByLine]);

  const handleQuoteInputKeyDown = (
    event: KeyboardEvent,
    lineNumber: number,
    quoteIndex: number,
    field: RfqQuoteField,
  ): void => {
    const rows = quoteRowsByLine[lineNumber] ?? [];
    const isLastRow = quoteIndex === rows.length - 1;

    if (event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      if (field === "quantity") {
        focusRfqQuoteInput(lineNumber, quoteIndex, "quoted_unit_price");
        return;
      }
      if (field === "quoted_unit_price") {
        focusRfqQuoteInput(lineNumber, quoteIndex, "quoted_lead_time_weeks");
        return;
      }
      if (!isLastRow) {
        focusRfqQuoteInput(lineNumber, quoteIndex + 1, "quantity");
        return;
      }
      pendingQuoteFocusRef.current = {
        lineNumber,
        quoteIndex: rows.length,
        field: "quantity",
      };
      addQuoteRow(lineNumber);
      return;
    }

    if (event.key !== "Enter" || field !== "quoted_lead_time_weeks") {
      return;
    }

    event.preventDefault();
    if (!isLastRow) {
      focusRfqQuoteInput(lineNumber, quoteIndex + 1, "quantity");
      return;
    }
    pendingQuoteFocusRef.current = {
      lineNumber,
      quoteIndex: rows.length,
      field: "quantity",
    };
    addQuoteRow(lineNumber);
  };

  const removeQuoteRow = (lineNumber: number, rowIndex: number): void => {
    setQuoteRowsByLine((current) => {
      const rows = current[lineNumber] ?? [];
      if (rows.length <= 1) return current;
      return {
        ...current,
        [lineNumber]: rows.filter((_, index) => index !== rowIndex),
      };
    });
  };

  const draftLines = useMemo(
    () => lines.map((line, index) => ({ ...line, lineNumber: index + 1 })),
    [lines],
  );

  const draftDirtyHint = useMemo(() => {
    if (!draftDirty) return null;
    return (
      rfqDraftDirtyFieldLabels(
        savedDraftSnapshot,
        internalNotes,
        externalNotes,
        neededByDate,
        currency,
        lines,
      ) ?? "Unsaved changes"
    );
  }, [
    draftDirty,
    savedDraftSnapshot,
    internalNotes,
    externalNotes,
    neededByDate,
    currency,
    lines,
  ]);

  const rfqCurrencyCode = useMemo(
    () => (rfq?.currency ?? currency).trim().toUpperCase() || "USD",
    [rfq?.currency, currency],
  );
  const rfqCurrencySymbol = useMemo(() => poCurrencySymbol(rfqCurrencyCode), [rfqCurrencyCode]);

  const readOnlyLines: MrpRfqLine[] = rfq?.lines ?? [];

  const closeOverflowMenu = useCallback(() => {
    setOverflowMenuOpen(false);
    setOverflowMenuPos(null);
  }, []);

  const updateOverflowMenuPosition = useCallback(() => {
    const trigger = overflowMenuButtonRef.current;
    if (!trigger) return;
    setOverflowMenuPos(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: 13 * 16,
        horizontalAlign: "end",
        gap: 4,
        maxHeightCap: Math.min(320, Math.floor(window.innerHeight * 0.88)),
      }),
    );
  }, []);

  useLayoutEffect(() => {
    if (!overflowMenuOpen) return;
    updateOverflowMenuPosition();
    const onScrollOrResize = (): void => {
      updateOverflowMenuPosition();
    };
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.visualViewport?.addEventListener("resize", onScrollOrResize);
    window.visualViewport?.addEventListener("scroll", onScrollOrResize);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.visualViewport?.removeEventListener("resize", onScrollOrResize);
      window.visualViewport?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [overflowMenuOpen, updateOverflowMenuPosition]);

  useEffect(() => {
    if (!overflowMenuOpen) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Element | null;
      if (target?.closest?.("[data-rfq-overflow-menu-panel], [data-rfq-overflow-menu-anchor]")) return;
      closeOverflowMenu();
    };
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === "Escape") closeOverflowMenu();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [overflowMenuOpen, closeOverflowMenu]);

  const showOverflowMenu =
    hubWritesAllowed &&
    !composeMode &&
    rfq != null &&
    (isDraft || (rfq.status !== "converted" && rfq.status !== "cancelled"));

  const actionBarBusy =
    createMutation.isPending ||
    draftSavePending ||
    sendMutation.isPending ||
    quoteMutation.isPending ||
    createPoMutation.isPending ||
    cancelMutation.isPending;

  const rfqBreadcrumbLabel = composeMode
    ? "New RFQ"
    : rfq?.rfq_number?.trim()
      ? `RFQ ${rfq.rfq_number.trim()}`
      : Number.isFinite(rfqId) && rfqId > 0
        ? `RFQ ${rfqId}`
        : "RFQ";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          {composeMode ? (
            <PageTitleWithHelp hoverHint={MRP_RFQ_STATUS_HOVER_HINT} breadcrumbLabel={rfqBreadcrumbLabel}>
              New RFQ
            </PageTitleWithHelp>
          ) : (
            <HubPageTitle breadcrumbLabel={rfqBreadcrumbLabel}>
              <span className="inline-flex items-center gap-1">
                {rfq?.rfq_number?.trim() || rfq?.id || "RFQ"}
                <HoverHintWithGlyph hoverHint={MRP_RFQ_STATUS_HOVER_HINT} />
              </span>
            </HubPageTitle>
          )}
          {!composeMode && rfq != null ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={mrpRfqStatusBadgeTone(rfq.status)}>{MRP_RFQ_STATUS_LABEL[rfq.status]}</Badge>
              {(rfq.freshdesk_ticket_ids ?? []).map((ticketId) => (
                <a
                  key={ticketId}
                  href={freshdeskTicketUrl(ticketId)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700"
                >
                  Freshdesk #{ticketId}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              ))}
              {rfq.converted_public_purchase_order_id != null ? (
                <PurchaseOrderHubLinkWithMegaventory
                  purchaseOrderId={rfq.converted_public_purchase_order_id}
                  poNumber={rfq.converted_po_number}
                  purchaseOrderMvId={null}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        {hubWritesAllowed ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isDraft && !composeMode ? (
              <DeferredCommitStatusGlyph
                pending={draftSavePending}
                isDirty={draftDirty}
                disabled={!hubWritesAllowed}
                commitError={
                  draftAutosaveError ??
                  (draftAutosaveStatus === "error" ? "Could not save this RFQ." : null)
                }
                commitSucceeded={draftSaveSucceeded}
                showSavedWhenIdle={true}
                savedHint="All changes saved"
                unsavedHint={draftDirtyHint ?? "Unsaved changes"}
              />
            ) : null}
            {!composeMode && rfq != null && rfq.status !== "cancelled" ? (
              <>
                <select
                  id="rfq-assignee"
                  aria-label="Assignee"
                  className="h-9 max-w-[11rem] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  value={rfq.assigned_to_user_id ?? ""}
                  disabled={
                    assignMutation.isPending
                    || takeOverMutation.isPending
                    || assigneeOptionsQuery.isLoading
                  }
                  onChange={(event) => {
                    const value = event.target.value;
                    assignMutation.mutate(value === "" ? null : value);
                  }}
                >
                  <option value="">Unassigned</option>
                  {assigneeOptions.map((option) => (
                    <option key={option.userId} value={option.userId}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={
                    assignMutation.isPending
                    || takeOverMutation.isPending
                    || (meQuery.isLoading && currentUserId === "")
                    || currentUserId === ""
                    || isAssignedToCurrentUser
                  }
                  onClick={() => takeOverMutation.mutate()}
                >
                  Take over
                </Button>
              </>
            ) : null}
            {composeMode ? (
              <Button type="button" onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
                Create RFQ
              </Button>
            ) : null}
            {isDraft && !composeMode ? (
              <Button
                type="button"
                onClick={() => setEmailModalOpen(true)}
                disabled={actionBarBusy}
              >
                Email RFQ
              </Button>
            ) : null}
            {canEnterQuote && !readOnly ? (
              <Button type="button" onClick={() => quoteMutation.mutate()} disabled={actionBarBusy}>
                Save quote
              </Button>
            ) : null}
            {canPickQuoteForPo ? (
              <Button type="button" onClick={() => createPoMutation.mutate()} disabled={actionBarBusy}>
                Create purchase order
              </Button>
            ) : null}
            {showOverflowMenu ? (
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="Your role has read-only access to the MRP hub module."
                layout="inline"
              >
                <Button
                  ref={overflowMenuButtonRef}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 shrink-0 p-0 text-slate-500 hover:text-slate-900"
                  data-rfq-overflow-menu-anchor
                  aria-label="More RFQ actions"
                  aria-haspopup="menu"
                  aria-expanded={overflowMenuOpen}
                  disabled={actionBarBusy}
                  onClick={() => {
                    if (overflowMenuOpen) {
                      closeOverflowMenu();
                      return;
                    }
                    setOverflowMenuOpen(true);
                  }}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </ReadOnlyHoverLayer>
            ) : null}
          </div>
        ) : null}
      </div>

      {actionError != null ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{actionError}</div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-1">
              Header
              <HoverHintWithGlyph
                hoverHint={
                  rfqDraftAutosaveEnabled
                    ? "Supplier defines who receives the RFQ. Notes and dates save automatically as you edit."
                    : "Supplier defines who receives the RFQ. Ship-to location is chosen when you create the purchase order."
                }
              />
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody className="grid gap-4 md:grid-cols-2">
          {composeMode ? (
            <div>
              <Label htmlFor="rfq-supplier">Supplier</Label>
              <SupplierSearchableSelect
                id="rfq-supplier"
                value={supplierId}
                searchValue={supplierSearch}
                onSearchChange={setSupplierSearch}
                onValueChange={(option) => {
                  setSupplierId(option?.value ?? "");
                  setSupplierLabel(option?.label ?? null);
                  if (option != null) setSupplierSearch("");
                }}
                placeholder="Search suppliers…"
                selectedLabelOverride={supplierLabel}
                disabled={!hubWritesAllowed}
              />
            </div>
          ) : (
            <div>
              <Label>Supplier</Label>
              <p className="mt-1 text-sm text-slate-900">{rfq?.supplier_name ?? "—"}</p>
            </div>
          )}
          <div>
            <Label htmlFor="rfq-needed-by">Needed by</Label>
            <Input
              id="rfq-needed-by"
              type="date"
              value={neededByDate}
              onChange={(event) => setNeededByDate(event.target.value)}
              onBlur={rfqDraftAutosaveEnabled ? handleRfqDraftFieldBlur : undefined}
              disabled={!hubWritesAllowed || readOnly || !isDraft}
            />
          </div>
          <div>
            <Label htmlFor="rfq-currency">Currency</Label>
            <p id="rfq-currency" className="mt-1 font-mono text-sm text-slate-900">{rfqCurrencyCode}</p>
            <p className="mt-0.5 text-xs text-slate-500">Locked to the supplier&apos;s currency.</p>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="rfq-internal-notes">Internal notes</Label>
            <TextArea
              id="rfq-internal-notes"
              value={internalNotes}
              onChange={(event) => setInternalNotes(event.target.value)}
              onBlur={rfqDraftAutosaveEnabled ? handleRfqDraftFieldBlur : undefined}
              disabled={!hubWritesAllowed || readOnly || !isDraft}
              rows={2}
              placeholder="Hub only — not included on supplier email"
            />
          </div>
          {!composeMode && rfq != null ? (
            <div className="md:col-span-2">
              <Label htmlFor="rfq-freshdesk-tickets">Freshdesk tickets</Label>
              <p className="mb-2 text-xs text-slate-500">Internal links only — not sent to the supplier.</p>
              <QualityFreshdeskTicketPicker
                id="rfq-freshdesk-tickets"
                values={rfq.freshdesk_ticket_ids ?? []}
                disabled={!hubWritesAllowed}
                onChange={async (ticketIds) => {
                  const current = new Set(rfq.freshdesk_ticket_ids ?? []);
                  const next = new Set(ticketIds);
                  const added = ticketIds.filter((ticketId) => !current.has(ticketId));
                  const removed = (rfq.freshdesk_ticket_ids ?? []).filter((ticketId) => !next.has(ticketId));
                  let latest: MrpRfq = rfq;
                  for (const ticketId of added) {
                    latest = await addMrpRfqFreshdeskTicket(rfqId, ticketId);
                  }
                  for (const ticketId of removed) {
                    latest = await removeMrpRfqFreshdeskTicket(rfqId, ticketId);
                  }
                  queryClient.setQueryData(["mrp", "rfqs", rfqId], latest);
                }}
              />
            </div>
          ) : null}
          <div className="md:col-span-2">
            <Label htmlFor="rfq-external-notes">External notes</Label>
            <TextArea
              id="rfq-external-notes"
              value={externalNotes}
              onChange={(event) => setExternalNotes(event.target.value)}
              onBlur={rfqDraftAutosaveEnabled ? handleRfqDraftFieldBlur : undefined}
              disabled={!hubWritesAllowed || readOnly || !isDraft}
              rows={2}
              placeholder="Included on supplier email"
            />
          </div>
          {canEnterQuote ? (
            <>
              <div>
                <Label htmlFor="rfq-supplier-quote-ref">Supplier quote reference</Label>
                <Input
                  id="rfq-supplier-quote-ref"
                  value={supplierQuoteReference}
                  onChange={(event) => setSupplierQuoteReference(event.target.value)}
                  disabled={!hubWritesAllowed || readOnly}
                />
              </div>
              <div>
                <Label htmlFor="rfq-quote-valid-until">Quote valid until</Label>
                <Input
                  id="rfq-quote-valid-until"
                  type="date"
                  value={quoteValidUntil}
                  onChange={(event) => setQuoteValidUntil(event.target.value)}
                  disabled={!hubWritesAllowed || readOnly}
                />
              </div>
            </>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>
            <span className="inline-flex items-center gap-1">
              Lines
              <HoverHintWithGlyph hoverHint="Each line lists target quantities you want quoted. After the RFQ is sent, enter supplier responses at any quantity—rows that match a target are flagged." />
            </span>
          </CardTitle>
          {isDraft && hubWritesAllowed ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setLines((current) => [...current, emptyRfqLineInput()])}
            >
              Add line
            </Button>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-6">
          {(isDraft ? draftLines : readOnlyLines).map((line, lineIndex) => {
            const lineNumber = isDraft ? lineIndex + 1 : (line as MrpRfqLine).line_number;
            const skuLine = isDraft ? (line as (typeof draftLines)[number]) : null;
            const readLine = !isDraft ? (line as MrpRfqLine) : null;
            const targets = isDraft
              ? (skuLine?.targets ?? [emptyRfqTargetInput()])
              : (readLine?.targets ?? []);
            const quoteRows = quoteRowsByLine[lineNumber] ?? [];
            const savedQuotes = readLine?.quotes ?? [];
            const draftLineSku = (skuLine?.sku ?? "").trim();
            const persistedDraftLine =
              isDraft && !composeMode
                ? (rfq?.lines.find((item) => item.line_number === lineNumber) ?? rfq?.lines[lineIndex] ?? null)
                : null;
            const lineForAttachments = readLine ?? persistedDraftLine;

            return (
              <div key={isDraft ? `draft-line-${lineIndex}` : `line-${lineNumber}`} className="rounded-md border border-slate-200">
                <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 bg-slate-50 px-3 py-2">
                  <span className="font-mono text-xs text-slate-500">#{lineNumber}</span>
                  {isDraft && hubWritesAllowed ? (
                    <>
                      <AssemblySkuCombobox
                        value={lines[lineIndex]?.sku ?? ""}
                        onChange={(next) => updateLine(lineIndex, { sku: next })}
                        onBlurCommittedWithSku={(trimmed) => {
                          void (async () => {
                            const currentDescription = lines[lineIndex]?.description ?? "";
                            const prefilledDescription = await prefillLineDescriptionFromSkuCatalog(
                              trimmed,
                              currentDescription,
                            );
                            updateLine(lineIndex, {
                              sku: trimmed,
                              description: prefilledDescription,
                            });
                          })();
                        }}
                        enforceCatalogSku
                        placeholder="Search SKU…"
                        className="max-w-xs font-mono text-xs"
                      />
                      <Input
                        value={lines[lineIndex]?.description ?? ""}
                        onChange={(event) => updateLine(lineIndex, { description: event.target.value })}
                        placeholder="Description"
                        className="min-w-[12rem] flex-1 text-sm"
                      />
                      <Input
                        value={lines[lineIndex]?.internal_notes ?? ""}
                        onChange={(event) =>
                          updateLine(lineIndex, { internal_notes: event.target.value })
                        }
                        placeholder="Internal line note"
                        className="min-w-[10rem] flex-1 text-sm"
                      />
                      <Input
                        value={lines[lineIndex]?.external_notes ?? ""}
                        onChange={(event) =>
                          updateLine(lineIndex, { external_notes: event.target.value })
                        }
                        placeholder="External line note"
                        className="min-w-[10rem] flex-1 text-sm"
                      />
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-sm">{readLine!.sku}</span>
                      <span className="text-sm text-slate-600">{readLine!.description || "—"}</span>
                      {readLine!.internal_notes != null && readLine!.internal_notes.trim() !== "" ? (
                        <span className="text-sm text-slate-500">
                          Internal: {readLine!.internal_notes}
                        </span>
                      ) : null}
                      {readLine!.external_notes != null && readLine!.external_notes.trim() !== "" ? (
                        <span className="text-sm text-slate-500">
                          External: {readLine!.external_notes}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>

                <div className="grid gap-4 p-3 md:grid-cols-2">
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Target quantities</p>
                      {isDraft && hubWritesAllowed ? (
                        <Button type="button" size="sm" variant="secondary" onClick={() => addLineTarget(lineIndex)}>
                          Add target
                        </Button>
                      ) : null}
                    </div>
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="py-1 pr-2">Qty</th>
                          <th className="py-1 pr-2 text-right">Target price</th>
                          {isDraft && hubWritesAllowed ? <th className="py-1" /> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {targets.map((target, targetIndex) => (
                          <tr key={`target-${lineNumber}-${targetIndex}`}>
                            <td className="py-1 pr-2">
                              {isDraft && hubWritesAllowed ? (
                                <Input
                                  id={rfqTargetInputId(lineIndex, targetIndex, "quantity")}
                                  type="text"
                                  inputMode="numeric"
                                  value={
                                    focusedTargetQuantityKey ===
                                    rfqTargetQuantityFocusKey(lineIndex, targetIndex)
                                      ? String(
                                          lines[lineIndex]?.targets[targetIndex]?.quantity ??
                                            target.quantity ??
                                            "",
                                        )
                                      : formatTargetQuantityInput(
                                          lines[lineIndex]?.targets[targetIndex]?.quantity ??
                                            target.quantity ??
                                            "",
                                        )
                                  }
                                  onChange={(event) =>
                                    updateLineTarget(lineIndex, targetIndex, {
                                      quantity: parseRfqTargetQuantityInput(event.target.value),
                                    })
                                  }
                                  onKeyDown={(event) =>
                                    handleTargetInputKeyDown(event, lineIndex, targetIndex, "quantity")
                                  }
                                  onFocus={(event) => {
                                    setFocusedTargetQuantityKey(
                                      rfqTargetQuantityFocusKey(lineIndex, targetIndex),
                                    );
                                    selectInputContents(event);
                                  }}
                                  onBlur={() => setFocusedTargetQuantityKey(null)}
                                  className="w-24 font-mono text-xs"
                                />
                              ) : (
                                <span className="font-mono text-xs">
                                  {formatNumber(rfqTargetQuantityAsNumber(target.quantity))}
                                </span>
                              )}
                            </td>
                            <td className="py-1 pr-2 text-right">
                              {isDraft && hubWritesAllowed ? (
                                <Input
                                  id={rfqTargetInputId(lineIndex, targetIndex, "price")}
                                  type="number"
                                  min={0}
                                  step="0.0001"
                                  value={lines[lineIndex]?.targets[targetIndex]?.target_unit_price ?? ""}
                                  onChange={(event) =>
                                    updateLineTarget(lineIndex, targetIndex, {
                                      target_unit_price:
                                        event.target.value.trim() === ""
                                          ? null
                                          : Number.parseFloat(event.target.value),
                                    })
                                  }
                                  onKeyDown={(event) =>
                                    handleTargetInputKeyDown(event, lineIndex, targetIndex, "price")
                                  }
                                  className="w-28 text-right font-mono text-xs"
                                />
                              ) : (
                                <span className="font-mono text-xs">
                                  {formatPoMoney(
                                    "target_unit_price" in target ? target.target_unit_price : null,
                                    rfq?.currency ?? "USD",
                                  )}
                                </span>
                              )}
                            </td>
                            {isDraft && hubWritesAllowed ? (
                              <td className="py-1 text-right">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => removeLineTarget(lineIndex, targetIndex)}
                                  disabled={(lines[lineIndex]?.targets.length ?? 1) <= 1}
                                >
                                  Remove
                                </Button>
                              </td>
                            ) : null}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(canEnterQuote || rfq?.status === "converted") && !isDraft ? (
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Supplier quotes</p>
                        {canEnterQuote && hubWritesAllowed && !readOnly ? (
                          <Button type="button" size="sm" variant="secondary" onClick={() => addQuoteRow(lineNumber)}>
                            Add quote row
                          </Button>
                        ) : null}
                      </div>
                      <table className="min-w-full text-left text-sm">
                        <thead className="text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            {canPickQuoteForPo && hubWritesAllowed ? <th className="py-1 pr-2">PO</th> : null}
                            <th className="py-1 pr-2 text-right">Qty</th>
                            <th className="py-1 pr-2 text-right">Quoted price ({rfqCurrencyCode})</th>
                            <th className="py-1 pr-2 text-right">Lead time (wks)</th>
                            <th className="py-1">Match</th>
                            {canEnterQuote && hubWritesAllowed && !readOnly ? <th className="py-1" /> : null}
                          </tr>
                        </thead>
                        <tbody>
                          {canPickQuoteForPo && hubWritesAllowed ? (
                            <tr>
                              <td className="py-1 pr-2">
                                <input
                                  type="radio"
                                  name={`po-quote-line-${lineNumber}`}
                                  checked={selectedQuoteIds[lineNumber] === null}
                                  onChange={() =>
                                    setSelectedQuoteIds((current) => ({
                                      ...current,
                                      [lineNumber]: null,
                                    }))
                                  }
                                  aria-label={`Skip line ${lineNumber} on purchase order`}
                                />
                              </td>
                              <td
                                className="py-1 text-xs text-slate-600"
                                colSpan={
                                  3
                                  + (canEnterQuote && hubWritesAllowed && !readOnly ? 1 : 0)
                                  + 1
                                }
                              >
                                Skip line on PO
                              </td>
                            </tr>
                          ) : null}
                          {(canEnterQuote && hubWritesAllowed && !readOnly ? quoteRows : savedQuotes).map(
                            (quoteRow, quoteIndex) => {
                              const savedQuote = savedQuotes[quoteIndex];
                              const quoteId = savedQuote?.id;
                              const matchesTarget =
                                savedQuote?.matches_target ??
                                (() => {
                                  const qty = Number.parseFloat(
                                    ("clientKey" in quoteRow ? quoteRow.quantity : String(quoteRow.quantity)).replace(
                                      /,/g,
                                      "",
                                    ),
                                  );
                                  return targets.some(
                                    (target) => rfqTargetQuantityAsNumber(target.quantity) === qty,
                                  );
                                })();

                              return (
                                <tr key={"clientKey" in quoteRow ? quoteRow.clientKey : `saved-${quoteId}`}>
                                  {canPickQuoteForPo && hubWritesAllowed ? (
                                    <td className="py-1 pr-2">
                                      {quoteId != null ? (
                                        <input
                                          type="radio"
                                          name={`po-quote-line-${lineNumber}`}
                                          checked={selectedQuoteIds[lineNumber] === quoteId}
                                          onChange={() =>
                                            setSelectedQuoteIds((current) => ({
                                              ...current,
                                              [lineNumber]: quoteId,
                                            }))
                                          }
                                          aria-label={`Select quote at quantity ${savedQuote?.quantity}`}
                                        />
                                      ) : null}
                                    </td>
                                  ) : null}
                                  <td className="py-1 pr-2 text-right">
                                    {canEnterQuote && hubWritesAllowed && !readOnly && "clientKey" in quoteRow ? (
                                      <Input
                                        id={rfqQuoteInputId(lineNumber, quoteIndex, "quantity")}
                                        type="text"
                                        inputMode="numeric"
                                        value={
                                          focusedQuoteQuantityKey ===
                                          rfqQuoteQuantityFocusKey(lineNumber, quoteIndex)
                                            ? quoteRow.quantity
                                            : formatQuoteQuantityInput(quoteRow.quantity)
                                        }
                                        onChange={(event) =>
                                          updateQuoteRow(lineNumber, quoteIndex, {
                                            quantity: parseQuoteQuantityInput(event.target.value),
                                          })
                                        }
                                        onKeyDown={(event) =>
                                          handleQuoteInputKeyDown(event, lineNumber, quoteIndex, "quantity")
                                        }
                                        onFocus={(event) => {
                                          setFocusedQuoteQuantityKey(
                                            rfqQuoteQuantityFocusKey(lineNumber, quoteIndex),
                                          );
                                          selectInputContents(event);
                                        }}
                                        onBlur={() => setFocusedQuoteQuantityKey(null)}
                                        className="w-24 text-right font-mono text-xs"
                                      />
                                    ) : (
                                      <span className="block text-right font-mono text-xs">
                                        {formatNumber(
                                          "quantity" in quoteRow && typeof quoteRow.quantity === "number"
                                            ? quoteRow.quantity
                                            : Number.parseFloat(String(quoteRow.quantity).replace(/,/g, "")),
                                        )}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-1 pr-2 text-right">
                                    {canEnterQuote && hubWritesAllowed && !readOnly && "clientKey" in quoteRow ? (
                                      <div className="relative inline-block">
                                        <span
                                          className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-xs text-slate-500"
                                          aria-hidden="true"
                                        >
                                          {rfqCurrencySymbol}
                                        </span>
                                        <Input
                                          id={rfqQuoteInputId(lineNumber, quoteIndex, "quoted_unit_price")}
                                          type="text"
                                          inputMode="decimal"
                                          value={quoteRow.quoted_unit_price}
                                          onChange={(event) =>
                                            updateQuoteRow(lineNumber, quoteIndex, {
                                              quoted_unit_price: parseQuotePriceInput(event.target.value),
                                            })
                                          }
                                          onKeyDown={(event) =>
                                            handleQuoteInputKeyDown(
                                              event,
                                              lineNumber,
                                              quoteIndex,
                                              "quoted_unit_price",
                                            )
                                          }
                                          onFocus={selectInputContents}
                                          className="w-28 pl-6 text-right font-mono text-xs"
                                        />
                                      </div>
                                    ) : (
                                      <span className="font-mono text-xs">
                                        {formatPoMoneyWithSymbol(
                                          "quoted_unit_price" in quoteRow &&
                                            typeof quoteRow.quoted_unit_price === "number"
                                            ? quoteRow.quoted_unit_price
                                            : quoteRow.quoted_unit_price === ""
                                              ? null
                                              : Number.parseFloat(String(quoteRow.quoted_unit_price)),
                                          rfqCurrencyCode,
                                        )}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-1 pr-2 text-right">
                                    {canEnterQuote && hubWritesAllowed && !readOnly && "clientKey" in quoteRow ? (
                                      <Input
                                        id={rfqQuoteInputId(lineNumber, quoteIndex, "quoted_lead_time_weeks")}
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={quoteRow.quoted_lead_time_weeks}
                                        onChange={(event) =>
                                          updateQuoteRow(lineNumber, quoteIndex, {
                                            quoted_lead_time_weeks: event.target.value,
                                          })
                                        }
                                        onKeyDown={(event) =>
                                          handleQuoteInputKeyDown(
                                            event,
                                            lineNumber,
                                            quoteIndex,
                                            "quoted_lead_time_weeks",
                                          )
                                        }
                                        onFocus={selectInputContents}
                                        className="w-20 text-right font-mono text-xs"
                                      />
                                    ) : (
                                      <span className="font-mono text-xs">
                                        {formatQuoteLeadTimeWeeks(
                                          "quoted_lead_time_weeks" in quoteRow
                                            ? quoteRow.quoted_lead_time_weeks
                                            : savedQuote?.quoted_lead_time_weeks,
                                        )}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-1">
                                    <RfqQuoteMatchBadge matchesTarget={matchesTarget} />
                                  </td>
                                  {canEnterQuote && hubWritesAllowed && !readOnly && "clientKey" in quoteRow ? (
                                    <td className="py-1 text-right">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => removeQuoteRow(lineNumber, quoteIndex)}
                                        disabled={quoteRows.length <= 1}
                                      >
                                        Remove
                                      </Button>
                                    </td>
                                  ) : null}
                                </tr>
                              );
                            },
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
                {lineForAttachments != null ? (
                  <div className="grid gap-3 border-t border-slate-100 bg-white p-3 md:grid-cols-2">
                    <RfqAttachmentsPanel
                      compact
                      title="Documents for supplier"
                      hoverHint={RFQ_LINE_SUPPLIER_DOCS_HOVER}
                      attachments={attachmentsByLineId.get(lineForAttachments.id)?.external ?? []}
                      disabled={!hubWritesAllowed || !canEditAttachments}
                      sku={lineForAttachments.sku}
                      {...attachmentPanelUpload({ audience: "external", lineId: lineForAttachments.id })}
                      onUpload={(file) =>
                        uploadMutation.mutate({ file, audience: "external", lineId: lineForAttachments.id })
                      }
                      onAttachFromDocument={(documentRecordIds) =>
                        attachDocumentMutation.mutateAsync({
                          documentRecordIds,
                          audience: "external",
                          lineId: lineForAttachments.id,
                        })
                      }
                      onDownload={(attachment) => void handleRfqAttachmentDownload(attachment)}
                      onResolvePreviewUrl={handleRfqAttachmentPreview}
                      onDelete={(attachment) => deleteAttachmentMutation.mutate(attachment.id)}
                    />
                    <RfqAttachmentsPanel
                      compact
                      title="Internal documents"
                      hoverHint={RFQ_LINE_INTERNAL_DOCS_HOVER}
                      attachments={attachmentsByLineId.get(lineForAttachments.id)?.internal ?? []}
                      disabled={!hubWritesAllowed || !canEditAttachments}
                      sku={lineForAttachments.sku}
                      {...attachmentPanelUpload({ audience: "internal", lineId: lineForAttachments.id })}
                      onUpload={(file) =>
                        uploadMutation.mutate({ file, audience: "internal", lineId: lineForAttachments.id })
                      }
                      onAttachFromDocument={(documentRecordIds) =>
                        attachDocumentMutation.mutateAsync({
                          documentRecordIds,
                          audience: "internal",
                          lineId: lineForAttachments.id,
                        })
                      }
                      onDownload={(attachment) => void handleRfqAttachmentDownload(attachment)}
                      onResolvePreviewUrl={handleRfqAttachmentPreview}
                      onDelete={(attachment) => deleteAttachmentMutation.mutate(attachment.id)}
                    />
                  </div>
                ) : composeMode && draftLineSku !== "" && documentsHubAccess ? (
                  <div className="grid gap-3 border-t border-slate-100 bg-white p-3 md:grid-cols-2">
                    <RfqAttachmentsPanel
                      compact
                      title="Documents for supplier"
                      hoverHint={RFQ_LINE_SUPPLIER_DOCS_HOVER}
                      attachments={[]}
                      disabled={!hubWritesAllowed}
                      uploadDisabled
                      uploadDisabledHint="Create the RFQ to upload files."
                      emptyListMessage={
                        composeStagedDocumentsEmptyMessage(
                          composeStagedExternalDocumentsByLineIndex[lineIndex]?.length ?? 0,
                        )
                      }
                      sku={draftLineSku}
                      onUpload={() => {}}
                      onAttachFromDocument={(documentRecordIds) => {
                        setComposeStagedExternalDocumentsByLineIndex((current) =>
                          mergeComposeStagedDocumentIds(current, lineIndex, documentRecordIds),
                        );
                      }}
                      onDownload={() => {}}
                      onResolvePreviewUrl={() =>
                        Promise.reject(new Error("Create the RFQ to preview attachments."))
                      }
                      onDelete={() => {}}
                    />
                    <RfqAttachmentsPanel
                      compact
                      title="Internal documents"
                      hoverHint={RFQ_LINE_INTERNAL_DOCS_HOVER}
                      attachments={[]}
                      disabled={!hubWritesAllowed}
                      uploadDisabled
                      uploadDisabledHint="Create the RFQ to upload files."
                      emptyListMessage={
                        composeStagedDocumentsEmptyMessage(
                          composeStagedInternalDocumentsByLineIndex[lineIndex]?.length ?? 0,
                        )
                      }
                      sku={draftLineSku}
                      onUpload={() => {}}
                      onAttachFromDocument={(documentRecordIds) => {
                        setComposeStagedInternalDocumentsByLineIndex((current) =>
                          mergeComposeStagedDocumentIds(current, lineIndex, documentRecordIds),
                        );
                      }}
                      onDownload={() => {}}
                      onResolvePreviewUrl={() =>
                        Promise.reject(new Error("Create the RFQ to preview attachments."))
                      }
                      onDelete={() => {}}
                    />
                  </div>
                ) : isDraft && draftLineSku !== "" && !documentsHubAccess ? (
                  <RfqLineDocumentAccessRow sku={draftLineSku} />
                ) : null}
              </div>
            );
          })}
        </CardBody>
      </Card>

      {!composeMode && rfq != null ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <RfqAttachmentsPanel
            title="Documents for supplier"
            hoverHint={RFQ_HEADER_SUPPLIER_DOCS_HOVER}
            attachments={headerExternalAttachments}
            disabled={!hubWritesAllowed || !canEditAttachments}
            {...attachmentPanelUpload({ audience: "external", lineId: null })}
            onUpload={(file) => uploadMutation.mutate({ file, audience: "external" })}
            onAttachFromDocument={(documentRecordIds) =>
              attachDocumentMutation.mutateAsync({
                documentRecordIds,
                audience: "external",
              })
            }
            onDownload={(attachment) => void handleRfqAttachmentDownload(attachment)}
            onResolvePreviewUrl={handleRfqAttachmentPreview}
            onDelete={(attachment) => deleteAttachmentMutation.mutate(attachment.id)}
          />
          <RfqAttachmentsPanel
            title="Internal documents"
            hoverHint={RFQ_HEADER_INTERNAL_DOCS_HOVER}
            attachments={headerInternalAttachments}
            disabled={!hubWritesAllowed || !canEditAttachments}
            {...attachmentPanelUpload({ audience: "internal", lineId: null })}
            onUpload={(file) => uploadMutation.mutate({ file, audience: "internal" })}
            onAttachFromDocument={(documentRecordIds) =>
              attachDocumentMutation.mutateAsync({
                documentRecordIds,
                audience: "internal",
              })
            }
            onDownload={(attachment) => void handleRfqAttachmentDownload(attachment)}
            onResolvePreviewUrl={handleRfqAttachmentPreview}
            onDelete={(attachment) => deleteAttachmentMutation.mutate(attachment.id)}
          />
        </div>
      ) : null}

      {emailModalOpen && Number.isFinite(rfqId) && rfqId > 0 ? (
        <RfqEmailModal
          rfqId={rfqId}
          onClose={() => setEmailModalOpen(false)}
          onSent={async () => {
            setActionError(null);
            await invalidateRfqQueries();
          }}
        />
      ) : null}

      {overflowMenuOpen && overflowMenuPos != null
        ? createPortal(
            <ul
              data-rfq-overflow-menu-panel
              role="menu"
              className="fixed overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm text-slate-800 shadow-lg"
              style={{
                top: overflowMenuPos.top,
                left: overflowMenuPos.left,
                width: overflowMenuPos.width,
                maxHeight: overflowMenuPos.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
              {isDraft ? (
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    className="block w-full px-3 py-2 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={sendMutation.isPending}
                    onClick={() => {
                      closeOverflowMenu();
                      sendMutation.mutate();
                    }}
                  >
                    Mark as sent
                  </button>
                </li>
              ) : null}
              {rfq != null && rfq.status !== "converted" && rfq.status !== "cancelled" ? (
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    className="block w-full px-3 py-2 text-left text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={cancelMutation.isPending}
                    onClick={() => {
                      closeOverflowMenu();
                      cancelMutation.mutate();
                    }}
                  >
                    Cancel RFQ
                  </button>
                </li>
              ) : null}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
